import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import EventCard from '@/components/EventCard';
import NoEvent from '@/icons/EmptyState/NoEvent';
import Twitter from '@/icons/Twitter';
import Linkedin from '@/icons/Linkedin';
import Instagram from '@/icons/Instagram';
import Follow from '@/icons/Follow';
import Location from '@/icons/Location';
import { fetchEventsByOrganizer, fetchEventsByUser, fetchOrganizerEventById, fetchUserProfileById, searchEvents } from '@/apiEndpoints';
import Avatar from '@/components/Avatar';
import MobileBottomNav from '@/components/MobileBottomNav';
import { wrap, pageBg, glow, eyebrow, Breadcrumbs, EmptyState, Segmented } from '@/components/ui/design';

export default function UserProfile() {
    const { id } = useParams();
    const [tab, setTab] = useState<'Upcoming' | 'Past'>('Upcoming');
    const [isFollowing, setIsFollowing] = useState(true); // TODO: connect to backend follow API

    // Fetch user profile
    const { data: user, isLoading: isUserLoading, isError: isUserError } = useQuery({
        queryKey: ['user', id],
        queryFn: () => fetchUserProfileById(id as string),
        enabled: !!id,
    });

    // Fetch user events (as user, not organizer)
    const { data: eventsData = { data: [] }, isLoading: isEventsLoading, isError: isEventsError } = useQuery({
        queryKey: ['userEvents', id],
        queryFn: () => fetchEventsByUser(id as string),
        enabled: !!id,
    });
    const events = Array.isArray(eventsData?.data) ? eventsData.data : [];

    // Filter events by isPast (if available)
    const filteredEvents = events.filter((e: any) => (tab === 'Upcoming' ? e.times[0].startDate > new Date().toISOString() : e.times[0].startDate < new Date().toISOString()));

    // Socials (mock for now, can be extended from user data)
    const socials = [
        { icon: <Instagram />, url: '#' },
        { icon: <Twitter />, url: '#' },
        { icon: <Linkedin />, url: '#' },
    ];

    return (
        <div className={pageBg}>
            <Header />
            <div className="relative isolate w-full min-h-screen flex flex-col overflow-x-clip">
                <div className={glow} />
                <div className={`${wrap} pt-6 pb-16 sm:pt-10 flex flex-col gap-8 lg:flex-row lg:gap-12`}>
                    <aside className="w-full lg:w-80 flex-shrink-0">
                        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: user?.fullName || 'User' }]} />
                        <div className="mt-6 rounded-[28px] bg-grey99 p-6 sm:p-8 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06] flex flex-col items-center text-center lg:sticky lg:top-24">
                            <Avatar src={user?.profileImage} name={user?.fullName} seed={id} size={112} className="ring-4 ring-white dark:ring-nightBg mb-5" />
                            <div className="text-[clamp(28px,4vw,36px)] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white mb-3 break-words max-w-full">{user?.fullName || 'User'}</div>
                            <div className="text-grey40 dark:text-nightMuted text-sm mb-5 flex items-center gap-1.5">
                                <Location />
                                {user?.address || '-'}
                            </div>
                            <div className="flex gap-2">
                                {socials.map((s, i) => (
                                    <a key={i} href={s.url} className="w-10 h-10 flex items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:ring-white/10 dark:hover:bg-white/10">{s.icon}</a>
                                ))}
                            </div>
                        </div>
                    </aside>
                    <main className="flex-1 min-w-0">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                            <div>
                                <p className={eyebrow}>Events</p>
                                <h1 className="mt-3 text-[clamp(36px,5.5vw,72px)] font-bold leading-[0.95] tracking-[-0.045em] text-grey10 dark:text-white">{tab}.</h1>
                            </div>
                            <Segmented
                                label="Events"
                                options={[{ key: 'Upcoming', label: 'Upcoming' }, { key: 'Past', label: 'Past' }]}
                                value={tab}
                                onChange={(key) => setTab(key as 'Upcoming' | 'Past')}
                            />
                        </div>
                        {isEventsLoading ? (
                            <div className="w-full flex justify-center items-center py-12 text-grey40 dark:text-nightMuted text-base">Loading events...</div>
                        ) : filteredEvents.length > 0 ? (
                            <div className="mt-4 grid grid-cols-1 content-start sm:-mx-4 sm:grid-cols-2 xl:grid-cols-3">
                                {filteredEvents.map((event: any, i: number) => (
                                    <EventCard key={i} event={event} userInfo={user} organizerInfo={null} />
                                ))}
                            </div>
                        ) : (
                            <div className="mt-8">
                                <EmptyState title={<span className="flex flex-col items-center"><NoEvent /><span className="mt-2">There is no event</span></span>} />
                            </div>
                        )}
                    </main>
                </div>
            </div>
            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
} 